import React, { useMemo, useRef } from 'react';
import { Button, Modal, Spinner } from 'react-bootstrap';
import { Tooltip } from '@mui/material';
import DataTable from 'react-data-table-component';
import queueDatatableCustomStyles from '../../Layout/Elements/QueueDatatableCustomStyles';
import DateRangePicker from 'rsuite/DateRangePicker';
import { Stack } from 'rsuite';
import subDays from 'date-fns/subDays';
import startOfWeek from 'date-fns/startOfWeek';
import endOfWeek from 'date-fns/endOfWeek';
import addDays from 'date-fns/addDays';
import startOfMonth from 'date-fns/startOfMonth';
import endOfMonth from 'date-fns/endOfMonth';
import addMonths from 'date-fns/addMonths';
import { setHours, setMinutes, setSeconds, startOfYear, endOfYear } from 'date-fns';
import Select from 'react-select';

import 'rsuite/dist/rsuite.min.css';
import { BiReset, BiSearch } from 'react-icons/bi';

const CustomeLogModal = ({
    filterData,
    userSelectlist,
    handleChangeFilter,
    show,
    onClose,
    logData = [],
    loading = false,
    totalRows = 0,
    limit = 25,
    onPageChange,
    onRowsPerPageChange,
    onRowClick,
    colorRow = {},
    customColumns = null,
    title = 'Scheduler Logs',
    isShowFilter=true
}) => {
    const malContRef = useRef(null)
        const formatOptionLabel = ({ value, label, is_logged_in }) => (
        <div className='d-flex flex-row justify-content-between align-items-center'>
            <div>{label}</div>
            {is_logged_in ?
                <div className='ms-2 dot' />

                :
                <div></div>
            }
        </div>
    );
        const predefinedBottomRanges = [
        {
            label: 'Today',
            value: [new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)],
            placement: 'left'
        },
        {
            label: 'Yesterday',
            value: [addDays(new Date().setHours(0, 0, 0, 0), -1), addDays(new Date().setHours(23, 59, 59, 59), -1)],
            placement: 'left'
        },
        {
            label: 'This week',
            value: [startOfWeek(new Date().setHours(0, 0, 0, 0)), new Date()],
            placement: 'left'
        },
        {
            label: 'Last 7 days',
            value: [subDays(new Date().setHours(0, 0, 0, 0), 6), new Date()],
            placement: 'left'
        },
        {
            label: 'Last 30 days',
            value: [subDays(new Date().setHours(0, 0, 0, 0), 29), new Date()],
            placement: 'left'
        },
        {
            label: 'This month',
            value: [startOfMonth(new Date().setHours(0, 0, 0, 0)), new Date()],
            placement: 'left'
        },
        {
            label: 'Last month',
            value: [startOfMonth(addMonths(new Date().setHours(0, 0, 0, 0), -1)), endOfMonth(addMonths(new Date(), -1))],
            placement: 'left'
        },
        {
            label: 'This year',
            value: [new Date(new Date().getFullYear(), 0, 1).setHours(0, 0, 0, 0), new Date()],
            placement: 'left'
        },
        {
            label: 'Last year',
            value: [setSeconds(setMinutes(setHours(startOfYear(new Date(new Date().getFullYear() - 1, 0, 1)), 0), 0), 0), setSeconds(setMinutes(setHours(endOfYear(new Date(new Date().getFullYear(), 0, 0)), 23), 59), 59)],
            placement: 'left'
        },

        {
            label: 'Last week',
            closeOverlay: false,
            value: value => {
                const [start = new Date().setHours(0, 0, 0, 0)] = value || [];
                return [
                    addDays(startOfWeek(start, { weekStartsOn: 0 }), -7),
                    addDays(endOfWeek(start, { weekStartsOn: 0 }), -7)
                ];
            },
            appearance: 'default'
        },
        {
            label: 'Next week',
            closeOverlay: false,
            value: value => {
                const [start = new Date()] = value || [];
                return [
                    addDays(startOfWeek(start, { weekStartsOn: 0 }), 7),
                    addDays(endOfWeek(start, { weekStartsOn: 0 }), 7)
                ];
            },
            appearance: 'default'
        }
    ];
    const conditionalRowStyles = [
        {
            when: (row, index) => index % 2 === 0 || colorRow?.id === row?.id,
            style: (row) => ({
                backgroundColor: colorRow?.id === row.id ? '#dbf4f5' : '#f2f2f2',
            }),
        },
    ];

    const defaultColumns = useMemo(
        () => [
            {
                id: 'created_by',
                name: 'User',
                selector: (row) => row?.created_by,
                cell: (row) => (
                    <div className="overflow-hidden d-flex flex-row  flex-wrap  text-truncate" style={{ width: "150px", overflow: 'hidden',textAlign:'center' }}>
                        <Tooltip 
                            title={
                                <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                                    {row?.created_by || ''}
                                </div>
                            } 
                            placement="top" 
                            arrow
                            disableInteractive={false}
                        >
                            <span style={{width:'100%'}} className="text-truncate">{row?.created_by}</span>
                        </Tooltip>
                    </div>
                ),
                sortable: false,
                width: '150px',
                center:true
            },
            { 
                name: 'Date', 
                selector: (row) => row?.created_date, 
                sortable: false,
                width: '100px',
                center:true
            },
            {
                id: 'log_data',
                name: 'Log Data',
                selector: (row) => row?.log_data,
                cell: (row) => (
                    <div className="overflow-hidden d-flex flex-row  flex-wrap  text-truncate" style={{ width: "450px", overflow: 'hidden',textAlign:'center' }}>
                        <Tooltip 
                            title={
                                <div style={{ maxHeight: '250px', overflowY: 'auto', padding: '5px' }}>
                                    {row?.log_data || ''}
                                </div>
                            }
                            placement="top" 
                            arrow
                            disableInteractive={false}
                        >
                            <span style={{width:'100%'}} className="text-truncate">{row?.log_data} </span>
                        </Tooltip>
                    </div>
                ),
                sortable: false,
                center:true,
                wrap: true,
                grow: 1,
            },
        ],
        []
    );

    const columns = customColumns || defaultColumns;

    return (
        
        
        <Modal show={show} onHide={onClose} size="lg" centered>
            <div ref={malContRef}>
            
            <Modal.Header closeButton>
                <Modal.Title className="d-flex align-items-center justify-content-between">
                    {title}
                </Modal.Title>
            </Modal.Header>

            {/* Filters Section - Below Header */}
            {isShowFilter&&
                <div className="px-4 pb-3 pt-3">
                    <div className="row g-3">
                        {/* User Select */}
                        <div className="col-12 col-md-4">
                            <Select
                                options={userSelectlist}
                                formatOptionLabel={formatOptionLabel}
                                className="select-search"
                                classNamePrefix="select"
                                placeholder="Select User"
                                name="user"
                                isClearable  // onChange={formik.handleChange}
                                onChange={(e) => handleChangeFilter(e,'user')}
                                styles={{ control: styles => ({ ...styles, backgroundColor: "hsl(204deg 33.33% 97.06%)", borderStyle: "none", boxShadow: "none", borderRadius: "10px" }), indicatorSeparator: styles => ({ ...styles, width: "0px", minHeight: "20px" }), container: styles => ({ ...styles, borderRadius: "9px", marginLeft: "0px" }) }}
                        />
                        </div>

                        {/* Date Range Picker */}
                        <Tooltip disableInteractive={false} title={"Log Date"} placement="top" arrow>
                            <div className="col-12 col-md-4">
                                <Stack direction="column" spacing={8} alignItems="flex-start">
                                    <DateRangePicker
                                        container={() => malContRef?.current}
                                        ranges={predefinedBottomRanges}
                                        cleanable={true}
                                        defaultCalendarValue={[new Date().setHours(0, 0, 0, 0), new Date().setHours(23, 59, 59, 59)]}
                                        appearance="default"
                                        format={"MM/dd/yyyy HH:mm"}
                                        placement='auto'
                                        placeholder={"MM/DD/YYYY - MM/DD/YYYY"}
                                        className="form-date-control form-control-solid b-r-1 text-dark jobmonitor-datepicker"
                                        name="date"
                                        onChange={(e) => { handleChangeFilter(e,'date') }}
                                        />
                                </Stack>
                            </div>
                        </Tooltip>
                        <div className="col-12 col-md-4 align-items-center d-flex">
                            <Tooltip disableInteractive={false} title="Search" placement='top' arrow>
                                <Button onClick={(e)=>handleChangeFilter(e,'submit')} className="btn btn-light btn-active-primary ms-5" type="button">
                                    <span className="svg-icon svg-icon-3">
                                        <BiSearch />
                                    </span>

                                </Button>
                            </Tooltip>
                        </div>
                        {/* <div className="col-12 col-md-1">
                            <Tooltip disableInteractive title="Reset" placement='top' arrow>
                                <Button onClick={(e)=>handleChangeFilter(e,'reset')} className="btn btn-light btn-active-primary" type='submit'>
                                    <span className="svg-icon svg-icon-3">
                                        <BiReset />
                                    </span>
                                </Button>
                            </Tooltip>
                        </div> */}
                    </div>
                </div>
            }
            <Modal.Body style={{ maxHeight: '90vh', overflowY: 'auto' }}>
                {/* {loading ? (
                    <div
                        className="d-flex justify-content-center align-items-center"
                        style={{ minHeight: 200 }}
                    >
                        <Spinner animation="border" />
                    </div>
                ) : ( */}
                    <DataTable
                        className="custom-datatable w-100 p-0"
                        fixedHeader
                        data={logData}
                        progressPending={loading}
                        columns={columns}
                        pagination
                        paginationServer
                        paginationTotalRows={totalRows}
                        paginationPerPage={limit}
                        paginationRowsPerPageOptions={[25, 50, 100]}
                        onChangeRowsPerPage={onRowsPerPageChange}
                        onChangePage={onPageChange}
                        pointerOnHover
                        onRowClicked={onRowClick}
                        responsive
                        customStyles={queueDatatableCustomStyles}
                        conditionalRowStyles={conditionalRowStyles}
                    />
                {/* )} */}
            </Modal.Body>
            </div>
        </Modal>
    );
};

export default CustomeLogModal;